import React, { useMemo, useCallback } from 'react';
import classnames from 'clsx';

import { Input, version2 } from 'ui-components/components/input';

import {
  getValueWithMask,
  extractMaskFromValue,
  getFormattedMask,
  isAddingACharacter,
  isModifyingExistingData,
  removeCharactersUntilValidPosition,
} from './input-mask-helper';
import style from './input-with-mask.module.less';

/**
 *
 * @param {object} props
 * @param {string} [props.className]
 * @param {string} [props.name]
 * @param {boolean} [props.disabled]
 * @param {React.ReactNode} [props.inlineMessage]
 * @param {import('../../inline-message/types').InlineMessageType} [props.inlineMessageType]
 * @param {string} [props.label]
 * @param {string} props.mask
 * @param {string} [props.maskPlaceholder]
 * @param {string} [props.value]
 * @param {(value: string) => void} props.onChange
 * @param {(name: string, value: string) => void} [props.onBlur]
 * @param {() => void} [props.onFocus]
 * @param {boolean} [props.hideMask]
 * @param {boolean} [props.keepMaskCharacters] - When true, keeps non-placeholder mask characters (e.g., 'RT' in '999999999RT9999')
 * @returns {JSX.Element}
 */
export const InputWithMask = ({
  name = '',
  className = null,
  disabled = false,
  inlineMessage,
  inlineMessageType,
  label = '',
  mask,
  maskPlaceholder = 'x',
  onChange,
  onBlur,
  onFocus,
  value = '',
  hideMask = false,
  keepMaskCharacters = false,
}) => {
  const formattedMask = useMemo(() => getFormattedMask(mask, maskPlaceholder), [maskPlaceholder, mask]);
  const firstCharacterAvailable = useMemo(() => formattedMask.indexOf(maskPlaceholder), [formattedMask, maskPlaceholder]);

  const inputValue = useMemo(() => {
    if (keepMaskCharacters && value) {
      const cleanValue = extractMaskFromValue(value, formattedMask, maskPlaceholder);
      return getValueWithMask(cleanValue, formattedMask, maskPlaceholder);
    }
    return getValueWithMask(value, formattedMask, maskPlaceholder);
  }, [keepMaskCharacters, value, formattedMask, maskPlaceholder]);

  const maskValue = useMemo(() => formattedMask.slice(inputValue.length), [formattedMask, inputValue.length]);

  const isValidPosition = useCallback(
    (position) => formattedMask[position] === maskPlaceholder && position >= firstCharacterAvailable,
    [formattedMask, maskPlaceholder, firstCharacterAvailable],
  );

  const processRemovingCharacter = useCallback(
    (newInputValue) => {
      if (isValidPosition(newInputValue.length)) {
        return newInputValue;
      }
      return removeCharactersUntilValidPosition(newInputValue.length, newInputValue, isValidPosition);
    },
    [isValidPosition],
  );

  const processAddingCharacter = useCallback(
    (currentInputValue, newInputValue) => (isModifyingExistingData(currentInputValue, newInputValue) ? currentInputValue : newInputValue),
    [],
  );

  const processOperation = useCallback(
    (currentInputValue, newInputValue) =>
      isAddingACharacter(currentInputValue, newInputValue)
        ? processAddingCharacter(currentInputValue, newInputValue)
        : processRemovingCharacter(newInputValue),
    [processAddingCharacter, processRemovingCharacter],
  );

  const handleOnChange = useCallback(
    ({ target }) => {
      const newValidInputValue = processOperation(inputValue, target.value);
      if (newValidInputValue !== inputValue) {
        const valueToSend = keepMaskCharacters
          ? newValidInputValue
          : extractMaskFromValue(newValidInputValue, formattedMask, maskPlaceholder);
        onChange(valueToSend);
      }
    },
    [formattedMask, inputValue, maskPlaceholder, onChange, processOperation, keepMaskCharacters],
  );

  const handleOnBlur = useCallback(() => {
    onBlur?.(name, value);
  }, [name, onBlur, value]);

  return (
    <div className={classnames(style.wrapper, className)}>
      <span className={classnames(style.mask, { [style['without-label']]: !label })}>
        <span className={style['mask-filled']}>{inputValue}</span>
        {!hideMask && <span>{maskValue}</span>}
      </span>
      <Input
        disabled={disabled}
        inlineMessage={inlineMessage}
        inlineMessageType={inlineMessageType}
        label={label}
        maxLength={formattedMask.length}
        name={name}
        onBlur={handleOnBlur}
        onChange={handleOnChange}
        onFocus={onFocus}
        value={inputValue}
        version={version2}
      />
    </div>
  );
};
